import React, { Component, Fragment, HTMLAttributes } from 'react'
import PageContent from '#src/components/PageContent'
import CloseIcon from './close.svg'
import {
  NavActiveDepartmentFragment,
  SellableKeywordTypeaheadComponent
} from '#src/generated/graphql'
import { getSellableUrl } from '#src/services/sellable'
import autosuggestMatch from 'autosuggest-highlight/match'
import autosuggestParse from 'autosuggest-highlight/parse'
import debounce from 'lodash/debounce'
import { Link, RouteComponentProps, withRouter } from 'react-router-dom'
// @ts-ignore TODO: Write typings for react-scrolllock
import ScrollLock, { TouchScrollable } from 'react-scrolllock'
import { serializeSearchQuery } from '#src/services/search'
import { Cancelable } from 'lodash'
import classnames from 'classnames'
import styles from './styles.css'

interface KeywordSearchProps extends HTMLAttributes<HTMLDivElement>, RouteComponentProps<{}> {
  department?: NavActiveDepartmentFragment
  open: boolean
  onClose: () => void
}

interface KeywordSearchState {
  inputKeywords: string
  queryKeywords: string
}

/**
 * The keyword search panel initiates a new search within the active department (if any), otherwise globally.
 */
class KeywordSearch extends Component<KeywordSearchProps, KeywordSearchState> {
  public state = {
    inputKeywords: '',
    queryKeywords: ''
  }

  public debouncedSetQueryKeywords: ((keywords: string) => void) & Cancelable
  public keywordInput: React.RefObject<HTMLInputElement> = React.createRef()

  public constructor(props: KeywordSearchProps) {
    super(props)

    this.handleInputChange = this.handleInputChange.bind(this)
    this.setQueryKeywords = this.setQueryKeywords.bind(this)
    this.debouncedSetQueryKeywords = debounce(this.setQueryKeywords, 200)
  }

  public componentWillUnmount() {
    this.debouncedSetQueryKeywords.cancel()
  }

  public componentDidUpdate(prevProps: KeywordSearchProps) {
    // Focus the input when the search panel opens
    if (!prevProps.open && this.props.open) {
      if (this.keywordInput.current) {
        this.keywordInput.current.focus()
      }
    }
  }

  public setQueryKeywords(queryKeywords: string) {
    this.setState({
      queryKeywords
    })
  }

  public handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = e.target.value

    this.setState({
      inputKeywords: inputValue
    })

    this.debouncedSetQueryKeywords(inputValue)
  }

  public commitKeywordSearch(keywords: string, department: NavActiveDepartmentFragment) {
    const loc = serializeSearchQuery({
      // TODO: Implement department-level search
      categorySlug: department.topLevelCategories[0].slug,
      keywords: keywords
    })

    this.props.history.push({
      pathname: loc.pathname,
      search: loc.searchParams.toString()
    })

    this.props.onClose()
  }

  public render() {
    const { department, className, open, onClose } = this.props

    // TODO: Support global search?
    // This should never happen since the UI to open search is only rendered in the department nav
    if (!department) {
      return null
    }

    return (
      <>
        {open && <ScrollLock />}
        <SellableKeywordTypeaheadComponent
          variables={{
            query: {
              prefixSearch: this.state.queryKeywords,
              departmentId: department.id
            },
            first: 10
          }}
          skip={this.state.queryKeywords.length === 0}
        >
          {({ data }) => {
            const results = data && data.sellableTypeahead && data.sellableTypeahead.hits

            return (
              <div
                className={classnames(styles.overlay, className, {
                  [styles.closed]: !open
                })}
                onClick={onClose}
                data-testid="search-overlay"
              >
                <div className={styles.panel} onClick={e => e.stopPropagation()}>
                  <div className={styles.top} data-testid="search-panel">
                    <PageContent>
                      <div className={styles.closeContainer}>
                        <div
                          className={styles.close}
                          onClick={onClose}
                          data-testid="search-panel-close"
                        >
                          <CloseIcon className={styles.closeIcon} />{' '}
                          <span className={styles.closeLabel}>CLOSE</span>
                        </div>
                      </div>

                      <div className={styles.prompt}>What are you looking for?</div>

                      <form
                        action={''}
                        onSubmit={e => {
                          e.preventDefault()
                          this.commitKeywordSearch(this.state.inputKeywords, department)
                          this.keywordInput.current && this.keywordInput.current.blur()
                        }}
                      >
                        <input
                          title="Search"
                          ref={this.keywordInput}
                          type="search"
                          name="q"
                          placeholder={`Search in ${department.displayName.toLowerCase()}`}
                          value={this.state.inputKeywords}
                          onChange={this.handleInputChange}
                          className={styles.keywords}
                          data-testid="search-keyword-input"
                        />
                      </form>
                    </PageContent>
                  </div>

                  <TouchScrollable>
                    <div
                      className={classnames(styles.resultsContainer, {
                        [styles.noResults]: !results || !results.length
                      })}
                    >
                      <PageContent>
                        <ul className={styles.resultsList}>
                          {results &&
                            results.map(result => {
                              const keywords = this.state.queryKeywords
                              const matches = autosuggestMatch(result.title, keywords)
                              const parts = autosuggestParse(result.title, matches)

                              return (
                                <li className={styles.result} key={result.id}>
                                  <Link
                                    to={getSellableUrl(result.sellable)}
                                    onClick={onClose}
                                    className={styles.resultLink}
                                  >
                                    {parts.map(part => {
                                      if (part.highlight) {
                                        return <strong key={part.text}>{part.text}</strong>
                                      }

                                      return <Fragment key={part.text}>{part.text}</Fragment>
                                    })}
                                  </Link>
                                </li>
                              )
                            })}
                        </ul>
                      </PageContent>
                    </div>
                  </TouchScrollable>
                </div>
              </div>
            )
          }}
        </SellableKeywordTypeaheadComponent>
      </>
    )
  }
}

export default withRouter(KeywordSearch)
